<template>
	<div class="p-4 md:p-6 flex-1 overflow-y-auto font-sans">
		<!-- ── Section Header ── -->
		<div class="flex items-center justify-between mb-6 pb-5 border-b border-primary-100 dark:border-dark-600">
			<div class="flex items-center gap-3">
				<div
					class="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-400/15 flex items-center justify-center flex-shrink-0">
					<svg class="w-5 h-5 text-primary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
						<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
							d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
					</svg>
				</div>
				<div>
					<h1
						class="font-display text-xl md:text-2xl font-extrabold tracking-tight text-dark-700 dark:text-white">
						Revenue
					</h1>
					<p class="text-[13px] text-muted dark:text-muted-light mt-0.5">
						Payments, payouts & Account integration
					</p>
				</div>
			</div>
		</div>

		<Breadcrumbs />

		<div class="mt-5 space-y-5">
			<div v-if="!stripeStatus">
				<div v-if="checkingStripeStatus"
					class="bg-surface dark:bg-dark-700 rounded-card shadow-card border border-primary-100 dark:border-dark-600 flex items-center justify-center min-h-[160px]">
					<div class="flex items-center gap-3 text-[13px] text-muted dark:text-muted-light">
						<div
							class="w-5 h-5 rounded-full border-2 border-primary-100 border-t-primary-400 animate-spin" />
						Checking your account status...
					</div>
				</div>
				<!-- ── IDLE ── -->
				<template v-else>
					<div v-if="stripeState === 'idle'"
						class="bg-surface dark:bg-dark-700 rounded-card shadow-card border border-primary-100 dark:border-dark-600 overflow-hidden">
						<div class="flex min-h-[160px]">
							<div
								class="w-1.5 flex-shrink-0 bg-gradient-to-b from-primary-600 to-primary-400 rounded-l-card" />
							<div
								class="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 px-8 py-8 flex-1">
								<div>
									<span
										class="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-widest uppercase text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-400/15 px-3 py-1 rounded-full mb-4">
										Payments & Payouts
									</span>
									<h2 class="font-display text-[18px] font-bold text-dark-700 dark:text-white mb-2">
										Connect Stripe to Get Paid
									</h2>
									<p class="text-[13px] text-muted dark:text-muted-light max-w-sm leading-relaxed">
										Connect a Stripe account to receive payouts from ticket sales.
									</p>
								</div>
								<button @click="startConnect" :disabled="loading"
									class="flex-shrink-0 px-5 py-2.5 rounded-xl font-display font-bold text-[13px] bg-dark-700 dark:bg-primary-400 text-white hover:bg-primary-600 dark:hover:bg-primary-600 hover:-translate-y-0.5 shadow-card hover:shadow-teal disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none transition-all duration-200">
									{{ loading ? "Loading..." : "Connect Stripe" }}
								</button>
							</div>
						</div>
					</div>
				</template>
				<!-- ── ONBOARDING ── -->
				<div v-if="stripeState === 'onboarding'"
					class="bg-surface dark:bg-dark-700 rounded-card shadow-card border border-primary-100 dark:border-dark-600 p-6">
					<h2 class="font-display text-[16px] font-bold text-dark-700 dark:text-white mb-1">
						Set up your payments account
					</h2>
					<p class="text-[13px] text-muted dark:text-muted-light mb-4">
						Complete the form below to start receiving payouts
					</p>
					<div ref="stripeContainer" class="min-h-[500px]" />
					<div v-if="error"
						class="mt-3 px-4 py-2 rounded-xl text-[13px] font-medium bg-red-50 dark:bg-red-950/30 text-red-500 border border-red-200 dark:border-red-800/50">
						{{ error }}
					</div>
				</div>

				<!-- ── CONNECTED ── -->
				<div v-if="stripeState === 'connected'"
					class="bg-surface dark:bg-dark-700 rounded-card shadow-card border border-primary-100 dark:border-dark-600 overflow-hidden">
					<div class="flex min-h-[90px]">
						<div
							class="w-1.5 flex-shrink-0 bg-gradient-to-b from-primary-600 to-primary-400 rounded-l-card" />
						<div class="flex flex-wrap items-center justify-between gap-4 px-6 py-5 flex-1">
							<div class="flex items-center gap-3.5">
								<div
									class="w-10 h-10 rounded-xl bg-primary-50 dark:bg-primary-400/15 flex items-center justify-center flex-shrink-0">
									<svg class="w-5 h-5 text-primary-400" fill="none" viewBox="0 0 24 24"
										stroke="currentColor">
										<rect x="2" y="5" width="20" height="14" rx="2" stroke-width="1.8" />
										<path d="M2 10h20" stroke-width="1.8" />
									</svg>
								</div>
								<div>
									<p
										class="text-[14px] font-semibold text-dark-700 dark:text-white leading-none mb-1">
										Account connected
									</p>
									<p class="text-[11px] text-muted dark:text-muted-light font-mono">
										{{ stripeData.stripe_account_id }}
									</p>
								</div>
							</div>

							<!-- Status chips -->
							<div class="flex flex-wrap gap-3">
								<div v-for="chip in stripeChips" :key="chip.label"
									class="bg-primary-50 dark:bg-dark-600 rounded-xl px-4 py-3 border border-primary-100 dark:border-dark-500">
									<p
										class="text-[10px] font-bold uppercase tracking-widest text-muted dark:text-muted-light mb-1.5">
										{{ chip.label }}
									</p>
									<div class="flex items-center gap-1.5">
										<svg class="w-4 h-4 flex-shrink-0" :class="chip.value
											? 'text-primary-400'
											: 'text-muted dark:text-muted-light'
											" viewBox="0 0 24 24" fill="none">
											<circle cx="12" cy="12" r="10" fill="currentColor" />
											<path d="M7 12l4 4 6-7" stroke="white" stroke-width="2"
												stroke-linecap="round" />
										</svg>
										<span class="text-[13px] font-semibold" :class="chip.value
											? 'text-primary-600 dark:text-primary-400'
											: 'text-muted dark:text-muted-light'
											">
											{{ chip.displayValue }}
										</span>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- ── PAYOUT HISTORY ── -->
				<div v-if="stripeState === 'connected'"
					class="bg-surface dark:bg-dark-700 rounded-card shadow-card border border-primary-100 dark:border-dark-600 p-6">
					<div class="flex items-center justify-between mb-5">
						<h2 class="font-display text-[15px] font-bold text-dark-700 dark:text-white">
							Payout History
						</h2>
						<button @click="openWithdrawModal"
							class="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-display font-bold text-[13px] bg-primary-400 text-white hover:bg-primary-600 hover:-translate-y-0.5 shadow-teal transition-all duration-200 disabled:opacity-50"
							:disabled="loading">
							<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
									d="M12 19V5m-7 7l7-7 7 7" />
							</svg>
							Withdraw Funds
						</button>
					</div>

					<div v-if="loading" class="flex items-center justify-center py-10 gap-3">
						<div
							class="w-6 h-6 rounded-full border-2 border-primary-100 border-t-primary-400 animate-spin" />
						<span class="text-[13px] text-muted dark:text-muted-light">Loading payouts...</span>
					</div>

					<div v-else-if="error"
						class="px-4 py-3 rounded-xl text-[13px] font-medium bg-red-50 dark:bg-red-950/30 text-red-500 border border-red-200 dark:border-red-800/50">
						{{ error }}
					</div>

					<div v-else class="overflow-x-auto rounded-xl border border-primary-100 dark:border-dark-600">
						<table class="w-full border-separate border-spacing-0">
							<thead>
								<tr>
									<th v-for="h in [
										'Amount',
										'Date',
										'Status',
										'Reference ID',
										'',
									]" :key="h" class="text-left text-[11px] font-bold uppercase tracking-widest px-4 py-3 text-muted dark:text-muted-light bg-primary-50 dark:bg-dark-600 border-b border-primary-100 dark:border-dark-500">
										{{ h }}
									</th>
								</tr>
							</thead>
							<tbody>
								<template v-for="(payout, index) in payouts" :key="payout.id || index">
									<tr @click="toggleRow(payout.id)"
										class="hover:bg-primary-50 dark:hover:bg-dark-600/50 transition-colors cursor-pointer">
										<td
											class="px-4 py-3.5 text-[13px] font-semibold text-dark-700 dark:text-white border-b border-primary-50 dark:border-dark-600">
											{{ formatCurrency(payout.amount) }}
										</td>
										<td
											class="px-4 py-3.5 text-[13px] text-muted dark:text-muted-light border-b border-primary-50 dark:border-dark-600">
											{{ formatDate(payout.completed_at) }}
										</td>
										<td class="px-4 py-3.5 border-b border-primary-50 dark:border-dark-600">
											<span
												class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold"
												:class="payout.status === 'completed'
													? 'bg-primary-50 dark:bg-primary-400/15 text-primary-600 dark:text-primary-300'
													: payout.status === 'pending'
														? 'bg-accent-50 dark:bg-accent-400/10 text-accent-700 dark:text-accent-400'
														: 'bg-red-50 dark:bg-red-950/30 text-red-500'
													">
												{{ payout.status }}
											</span>
										</td>
										<td
											class="px-4 py-3.5 text-[12px] text-muted dark:text-muted-light font-mono border-b border-primary-50 dark:border-dark-600">
											{{ payout.transfer_id }}
										</td>
										<td
											class="px-4 py-3.5 text-right border-b border-primary-50 dark:border-dark-600">
											<div class="flex items-center justify-end gap-2">
												<button v-if="payout.status === 'failed'"
													@click.stop="retryWithdrawal(payout.id)"
													class="px-3 py-1 rounded-lg text-[12px] font-semibold bg-red-50 text-red-600 border border-red-200 hover:bg-red-100">
													Retry
												</button>

												<svg class="w-4 h-4 text-muted dark:text-muted-light transition-transform duration-200"
													:class="expandedRow === payout.id ? 'rotate-90' : ''" fill="none"
													stroke="currentColor" viewBox="0 0 24 24">
													<path stroke-linecap="round" stroke-linejoin="round"
														stroke-width="2" d="M9 5l7 7-7 7" />
												</svg>
											</div>
										</td>
									</tr>

									<!-- Expanded detail -->
									<tr v-if="expandedRow === payout.id">
										<td colspan="5"
											class="px-4 py-4 bg-primary-50/40 dark:bg-dark-600/40 border-b border-primary-100 dark:border-dark-600">
											<div v-if="loadingRow === payout.id"
												class="flex items-center gap-2 text-[13px] text-muted dark:text-muted-light">
												<div
													class="w-4 h-4 rounded-full border-2 border-primary-100 border-t-primary-400 animate-spin" />
												Loading details...
											</div>
											<div v-else-if="payoutDetails[payout.id]?.error" class="space-y-2">
												<p
													class="px-4 py-2 rounded-xl text-[13px] font-medium bg-red-50 dark:bg-red-950/30 text-red-500 border border-red-200 dark:border-red-800/50">
													{{ payoutDetails[payout.id].error }}
												</p>
												<button @click.stop="retryFetch(payout.id)"
													class="px-4 py-2 rounded-xl text-[12px] font-semibold bg-primary-50 dark:bg-dark-600 text-primary-600 dark:text-primary-400 border border-primary-100 dark:border-dark-500 hover:bg-primary-100 transition-colors">
													Retry
												</button>
											</div>
											<div v-else-if="payoutDetails[payout.id]"
												class="grid grid-cols-1 md:grid-cols-2 gap-3">
												<div v-for="event in payoutDetails[payout.id].events"
													:key="event.event_id"
													class="bg-surface dark:bg-dark-700 rounded-xl p-4 border border-primary-100 dark:border-dark-600">
													<p
														class="font-display font-semibold text-[13px] text-dark-700 dark:text-white mb-3">
														{{ event.event_title }}
													</p>
													<div class="grid grid-cols-2 gap-2">
														<div v-for="field in [
															{
																label: 'Total Sales',
																val: formatCurrency(event.gross_sales),
															},
															{
																label: 'transaction processing Fee',
																val: formatCurrency(event.stripe_fee),
															},
															{
																label: 'maintainance Fee',
																val: formatCurrency(event.platform_fee),
															},
															{
																label: 'Net Payout',
																val: formatCurrency(event.net_payout),
															},
														]" :key="field.label">
															<p
																class="text-[10px] font-bold uppercase tracking-widest text-muted dark:text-muted-light">
																{{ field.label }}
															</p>
															<p
																class="text-[13px] font-semibold text-dark-700 dark:text-white font-mono">
																{{ field.val }}
															</p>
														</div>
													</div>
												</div>
											</div>
										</td>
									</tr>
								</template>

								<tr v-if="!payouts.length">
									<td colspan="5"
										class="px-4 py-10 text-center text-[13px] text-muted dark:text-muted-light">
										No payouts found
									</td>
								</tr>
							</tbody>
						</table>
					</div>
				</div>
			</div>
		</div>
	</div>

	<!-- ══ WITHDRAW MODAL ══ -->
	<transition name="modal">
		<div v-if="showWithdrawModal"
			class="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4"
			@click.self="closeModal">
			<div
				class="bg-surface dark:bg-dark-700 w-full max-w-lg rounded-card shadow-dropdown border border-primary-100 dark:border-dark-600">
				<!-- Header -->
				<div class="flex items-center justify-between p-6 border-b border-primary-100 dark:border-dark-600">
					<div>
						<p
							class="text-[10px] font-bold uppercase tracking-widest text-muted dark:text-muted-light mb-0.5">
							Revenue
						</p>
						<h2 class="font-display text-[15px] font-bold text-dark-700 dark:text-white">
							Withdraw Funds
						</h2>
					</div>
					<button @click="closeModal"
						class="w-8 h-8 rounded-xl bg-primary-50 dark:bg-dark-600 flex items-center justify-center text-muted dark:text-muted-light hover:bg-primary-100 dark:hover:bg-dark-500 transition-colors">
						<svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5"
								d="M6 18L18 6M6 6l12 12" />
						</svg>
					</button>
				</div>

				<div class="p-6">
					<!-- STEP 1: SELECT EVENTS -->
					<div v-if="step === 'select'">
						<div v-if="loadingEvents"
							class="flex items-center gap-2 py-6 text-[13px] text-muted dark:text-muted-light">
							<div
								class="w-4 h-4 rounded-full border-2 border-primary-100 border-t-primary-400 animate-spin" />
							Loading events...
						</div>

						<div v-else-if="!events.length"
							class="py-8 text-center text-[13px] text-muted dark:text-muted-light">
							You'll be able to withdraw funds once your events are completed.
						</div>

						<div v-else>
							<p class="text-[13px] text-muted dark:text-muted-light mb-4">
								Select the events you want to withdraw funds for:
							</p>
							<div
								class="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto rounded-xl border border-primary-100 dark:border-dark-500 p-3 bg-primary-50/50 dark:bg-dark-600/50">
								<label v-for="event in events" :key="event.event_id"
									class="flex items-center gap-2.5 p-2 rounded-lg cursor-pointer hover:bg-primary-100 dark:hover:bg-dark-500 transition-colors">
									<input type="checkbox" :value="event.event_id" v-model="selectedEventId"
										@change="handleSelectEvent" class="sr-only peer" />
									<div
										class="w-4 h-4 rounded-md border-2 border-primary-200 dark:border-dark-400 bg-white dark:bg-dark-600 flex-shrink-0 peer-checked:bg-primary-400 peer-checked:border-primary-400 transition-colors" />
									<span
										class="text-[12px] font-medium text-dark-700 dark:text-white/80 leading-tight">
										{{ event.event_title }} —
										{{ formatCurrency(event.net_payout) }}
									</span>
								</label>
							</div>

							<div
								class="mt-4 px-4 py-3 rounded-xl bg-primary-50 dark:bg-dark-600 border border-primary-100 dark:border-dark-500 flex justify-between items-center">
								<span
									class="text-[12px] font-bold uppercase tracking-widest text-muted dark:text-muted-light">
									Total
								</span>
								<span class="font-display font-extrabold text-[16px] text-primary-400">
									{{ formatCurrency(totalAmount) }}
								</span>
							</div>
						</div>

						<div class="flex justify-end gap-3 mt-5 pt-4 border-t border-primary-100 dark:border-dark-600">
							<button @click="closeModal"
								class="px-5 py-2.5 rounded-xl text-[13px] font-semibold bg-primary-50 dark:bg-dark-600 text-dark-400 dark:text-white/70 border border-primary-100 dark:border-dark-500 hover:bg-primary-100 transition-colors">
								Cancel
							</button>
							<button @click="goToConfirm" :disabled="!selectedEvents.length || !events.length"
								class="px-5 py-2.5 rounded-xl text-[13px] font-bold bg-primary-400 text-white hover:bg-primary-600 hover:-translate-y-0.5 shadow-teal transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none">
								Withdraw
							</button>
						</div>
					</div>

					<!-- STEP 2: CONFIRM -->
					<div v-if="step === 'confirm'">
						<p class="text-[13px] font-semibold text-dark-700 dark:text-white mb-4">
							Confirm Withdrawal
						</p>
						<div class="space-y-2 mb-4">
							<div v-for="event in selectedEvents" :key="event.id"
								class="flex justify-between items-center px-4 py-3 rounded-xl bg-primary-50/60 dark:bg-dark-600/60 border border-primary-100 dark:border-dark-500 text-[13px]">
								<span class="font-medium text-dark-700 dark:text-white">{{
									event.title
								}}</span>
								<span class="font-semibold text-primary-400">{{
									formatCurrency(event.net_payout)
								}}</span>
							</div>
						</div>
						<div
							class="px-4 py-3 rounded-xl bg-primary-50 dark:bg-dark-600 border border-primary-100 dark:border-dark-500 flex justify-between items-center mb-4">
							<span
								class="text-[12px] font-bold uppercase tracking-widest text-muted dark:text-muted-light">Total</span>
							<span class="font-display font-extrabold text-[16px] text-primary-400">{{
								formatCurrency(totalAmount) }}</span>
						</div>

						<div v-if="withdrawError"
							class="px-4 py-3 rounded-xl text-[13px] font-medium mb-4 bg-red-50 dark:bg-red-950/30 text-red-500 border border-red-200 dark:border-red-800/50">
							{{ withdrawError }}
						</div>

						<div class="flex justify-end gap-3 pt-4 border-t border-primary-100 dark:border-dark-600">
							<button @click="step = 'select'"
								class="px-5 py-2.5 rounded-xl text-[13px] font-semibold bg-primary-50 dark:bg-dark-600 text-dark-400 dark:text-white/70 border border-primary-100 dark:border-dark-500 hover:bg-primary-100 transition-colors">
								← Back
							</button>
							<button @click="payoutRequest" :disabled="submitting"
								class="px-5 py-2.5 rounded-xl text-[13px] font-bold bg-primary-400 text-white hover:bg-primary-600 hover:-translate-y-0.5 shadow-teal transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed">
								{{ submitting ? "Processing..." : "Confirm" }}
							</button>
						</div>
					</div>
				</div>
			</div>
		</div>
	</transition>
</template>

<script>
import Breadcrumbs from "@/components/Breadcrumbs.vue";
import { apiDataService } from "@/services/apiDataService";
import { loadConnectAndInitialize } from "@stripe/connect-js";

export default {
	name: "RevenueView",
	components: { Breadcrumbs },
	data() {
		return {
			stripeState: "",
			checkingStripeStatus: true,
			stripeData: null,
			stripeClientId: null,
			loading: false,
			loadingEvents: false,
			error: null,
			payouts: [],
			expandedRow: null,
			payoutDetails: [],
			loadingRow: null,
			showWithdrawModal: false,
			step: "select",
			withdrawError: null,
			events: [],
			selectedEventId: [],
			selectedEvents: [],
			totalAmount: 0,
		};
	},
	computed: {
		stripeStatus() {
			return false;
		},
		stripeChips() {
			if (!this.stripeData) return [];
			return [
				{
					label: "Onboarding",
					value: this.stripeData.onboarding_status === "complete",
					displayValue:
						this.stripeData.onboarding_status === "complete"
							? "Complete"
							: "Pending",
				},
				{
					label: "Payouts",
					value: this.stripeData.payouts_enabled,
					displayValue: this.stripeData.payouts_enabled
						? "Enabled"
						: "Disabled",
				},
				{
					label: "Charges",
					value: this.stripeData.charges_enabled,
					displayValue: this.stripeData.charges_enabled
						? "Enabled"
						: "Disabled",
				},
				{
					label: "Details",
					value: this.stripeData.details_submitted,
					displayValue: this.stripeData.details_submitted
						? "Submitted"
						: "Pending",
				},
			];
		},
	},
	methods: {
		async toggleRow(id) {
			if (this.expandedRow === id) {
				this.expandedRow = null;
				return;
			}
			this.expandedRow = id;
			this.loadingRow = id;
			try {
				const res = await apiDataService.getPayoutDetails(id);
				this.payoutDetails[id] = res.data || [];
				
			} catch (err) {
				console.error(err);
				this.payoutDetails[id] = { error: "Failed to load details" };
			} finally {
				this.loadingRow = null;
			}
		},
		async retryFetch(id) {
			this.loadingRow = id;
			try {
				const res = await apiDataService.getPayoutDetails(id);
				this.payoutDetails[id] = res.data || [];
			} catch (err) {
				console.error(err);
				this.payoutDetails[id] = { error: "Retry failed. Try again." };
			} finally {
				this.loadingRow = null;
			}
		},
		formatCurrency(amount) {
			return new Intl.NumberFormat("en-IE", {
				style: "currency",
				currency: "EUR",
			}).format(amount);
		},
		formatDate(date) {
			return new Date(date).toLocaleString();
		},
		async startConnect() {
			this.loading = true;
			this.error = null;
			try {
				const res = await apiDataService.connectStripe();
				this.stripeClientId = res.data.account_id;
				this.stripeState = "onboarding";
				await this.$nextTick();
				await this.initializeStripeConnect();
			} catch (err) {
				console.error(err);
				this.error = "Failed to create Stripe account";
			} finally {
				this.loading = false;
			}
		},
		async getSpripeStatu() {
			this.checkingStripeStatus = true;
			this.error = null;
			try {
				const res = await apiDataService.stripeStatus();
				const data = res.data;
				if (data.onboarding_status === "complete") {
					this.stripeData = data;
					this.stripeState = "connected";
				} else if (data.onboarding_status === "pending") {
					this.startConnect();
				} else {
					this.stripeState = "idle";
				}
			} catch (err) {
				console.error(err);
				this.error = "Failed to fetch Stripe status";
			} finally {
				this.checkingStripeStatus = false;
			}
		},
		async initializeStripeConnect() {
			if (!this.$refs.stripeContainer) return;
			const publishableKey = process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY;
			if (!publishableKey) {
				this.error = "Stripe publishable key not configured";
				return;
			}
			try {
				const instance = await loadConnectAndInitialize({
					publishableKey,
					fetchClientSecret: async () => {
						const secretRes = await apiDataService.clientSecretEmbedding({
							account_id: this.stripeClientId,
						});
						return secretRes.data.client_secret;
					},
				});
				const onboarding = instance.create("account-onboarding");
				this.$refs.stripeContainer.appendChild(onboarding);
				onboarding.addEventListener("onboarding_exited", () => {
					this.stripeState = "connected";
				});
			} catch (err) {
				console.error("Stripe error:", err);
				this.error = err.message;
			}
		},
		async payoutHistory() {
			this.loading = true;
			this.error = null;
			try {
				const res = await apiDataService.getPayoutHistory();
				this.payouts = res.data || [];
			} catch (err) {
				console.error(err);
				this.error = "Failed to fetch payout history";
			} finally {
				this.loading = false;
			}
		},
		async openWithdrawModal() {
			this.showWithdrawModal = true;
			await this.fetchEvents();
		},
		closeModal() {
			this.showWithdrawModal = false;
			this.step = "select";
			this.selectedEventId = [];
			this.selectedEvents = [];
			this.totalAmount = 0;
			this.withdrawError = null;
		},
		handleSelectEvent() {
			this.selectedEvents = this.events.filter((event) =>
				this.selectedEventId.includes(event.event_id),
			);
			this.totalAmount = this.selectedEvents.reduce(
				(sum, event) => sum + (event.net_payout || 0),
				0,
			);
		},
		async fetchEvents() {
			try {
				this.loadingEvents = true;
				const res = await apiDataService.getPayoutEventList();
				this.events = res.data || [];
			} catch (err) {
				console.error(err);
			} finally {
				this.loadingEvents = false;
			}
		},
		goToConfirm() {
			if (!this.selectedEvents.length) return;
			this.step = "confirm";
		},
		async payoutRequest() {
			try {
				this.submitting = true;
				this.withdrawError = null;
				await apiDataService.requestPayout({ event_ids: this.selectedEventId });
				this.closeModal();
				this.payoutHistory();
			} catch (err) {
				console.error(err);
				this.withdrawError = "Failed to request payout. Try again.";
			} finally {
				this.submitting = false;
			}
		},
		async retryWithdrawal(payout) {
			try {
				this.loadingRow = payout.id;
				await apiDataService.retryRequestPayout(payout);
				this.payoutHistory();
			} catch (err) {
				console.error(err);
				this.error = "Failed to retry withdrawal. Try again.";
			} finally {
				this.loadingRow = null;
			}
		},
	},
	mounted() {
		this.getSpripeStatu();
		this.payoutHistory();
	},
};
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
	transition: opacity 0.2s ease;
}

.modal-enter-from,
.modal-leave-to {
	opacity: 0;
}
</style>
